import React, { useState, useEffect } from "react";
import { Layout }     from "./components/layout/Layout";
import LandingPage    from "./pages/Landing";
import FormPage, { INITIAL_FORM } from "./pages/FormPage";
import LoadingPage    from "./pages/LoadingPage";
import Dashboard      from "./pages/Dashboard";
import { injectTheme } from "./styles/theme";

const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_KEY;

export default function App() {
  const [page,   setPage]   = useState("landing");
  const [form,   setForm]   = useState(INITIAL_FORM);
  const [result, setResult] = useState(null);

  useEffect(() => { injectTheme(); }, []);

  // ── Scroll to top on page change ──
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  // ── Navigate helper ──
  const navigate = (target) => {
    if (target === "dashboard" && !result) {
      setPage("dashboard");
    } else {
      setPage(target);
    }
  };

  // ── Submit form → call Claude API ──
  const handleSubmit = async () => {
    setPage("loading");
    try {
      const prompt = buildPrompt(form);

      const resp = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": ANTHROPIC_API_KEY,                          // ✅ From .env
          "anthropic-version": "2023-06-01",                        // ✅ Required
          "anthropic-dangerous-direct-browser-access": "true",      // ✅ Required for browser
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          messages: [{ role: "user", content: prompt }],
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json();
        console.error("API Error Response:", errorData);
        throw new Error(`API failed: ${resp.status} — ${errorData?.error?.message || "Unknown error"}`);
      }

      const data = await resp.json();
      const text = data.content?.map(c => c.text || "").join("") || "";
      const clean = text.replace(/```json\n?|```\n?/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
    } catch (err) {
      console.error("API Error:", err);
      // Graceful fallback — still show dashboard with partial data
      setResult({
        topCareer: form.interestedField?.includes("IT") ? "Software Engineer" : "Business Analyst",
        successProbability: 74,
        riskLevel: "Medium",
        aiSummary: `Based on your profile from ${form.district || "your district"}, your skills align well with the ${form.interestedField || "IT"} sector. Focus on key skill gaps to maximize your work probability.`,
        fullAnalysis: `Your ${form.qualification || "degree"} from ${form.state || "your state"} places you in a competitive position. Given your technical skills (${form.techSkills?.join(", ") || "listed above"}), focus on in-demand technologies to close the gap with industry requirements.`,
        careerScores: [
          { name: form.interestedField?.split(" ")[0] || "Software Dev", score: 78 },
          { name: "Data Analyst",   score: 65 },
          { name: "Cloud Engineer", score: 60 },
          { name: "Govt Services",  score: 55 },
          { name: "Freelancer",     score: 70 },
        ],
        salaryData: [
          { year: "Now",  salary: 4   },
          { year: "2yr",  salary: 7   },
          { year: "5yr",  salary: 14  },
          { year: "10yr", salary: 26  },
        ],
        skillRadar: [
          { skill: "Technical",     A: 65, B: 90 },
          { skill: "Soft Skills",   A: 60, B: 80 },
          { skill: "Domain",        A: 55, B: 85 },
          { skill: "Communication", A: 70, B: 85 },
          { skill: "Leadership",    A: 50, B: 75 },
        ],
        pieData: [
          { name: "IT",        value: 42 },
          { name: "Govt",      value: 25 },
          { name: "Startups",  value: 18 },
          { name: "Business",  value: 10 },
          { name: "Others",    value: 5  },
        ],
        strengths:  ["Relevant technical skills","Good educational background","Multilingual advantage","Adaptable to work environments"],
        skillGaps:  ["Advanced DSA","Cloud platforms","Portfolio projects","System design concepts"],
        roadmap: [
          { phase:"Month 1–2", title:"Foundation",  steps:["DSA fundamentals","Core language mastery","GitHub setup"] },
          { phase:"Month 3–4", title:"Build",       steps:["Key framework course","2 portfolio projects","Networking & LinkedIn"] },
          { phase:"Month 5–6", title:"Launch",      steps:["Job applications","Resume optimization","Mock interviews"] },
          { phase:"Year 1+",   title:"Growth",      steps:["Upskill in cloud","Target senior roles","Mentorship & community"] },
        ],
        platforms:   ["NPTEL (Free IIT courses)","FreeCodeCamp","Google Career Certificates","Coursera (Aid available)","YouTube tutorials"],
        scholarships:["NSP – National Scholarship Portal","State Merit Scholarship","AICTE Pragati Scheme","PM-Yasasvi","Corporate CSR Grants"],
        trendingNow: ["Generative AI","Full Stack","Cloud & DevOps","Data Analytics","Cybersecurity"],
        futureTrends:["AI Engineering","Quantum Computing","Web3","AR/VR","Robotics"],
        localDemand: ["Web Developer","IT Support","Mobile App Dev","BPO Analyst","Govt Exam Prep"],
        globalDemand:["Cloud Architect","DevOps","Data Scientist","AI Researcher","Security Analyst"],
      });
    } finally {
      setPage("dashboard");
    }
  };

  // ── Reset all state ──
  const handleReset = () => {
    setForm(INITIAL_FORM);
    setResult(null);
    setPage("landing");
  };

  // ── Page content map ──
  const PageContent = {
    landing:   <LandingPage   onNavigate={navigate} />,
    form:      <FormPage      form={form} setForm={setForm} onSubmit={handleSubmit} />,
    loading:   <LoadingPage   />,
    dashboard: <Dashboard     result={result} form={form} onNavigate={navigate} />,
  };

  return (
    <Layout currentPage={page} onNavigate={navigate}>
      {PageContent[page] || PageContent.landing}
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROMPT BUILDER
// ─────────────────────────────────────────────────────────────────────────────
function buildPrompt(form) {
  return `You are an expert Indian AI career counselor. Analyze this student's profile and return a structured JSON career prediction report.

STUDENT PROFILE:
Name: ${form.fullName}, Age: ${form.age}, Gender: ${form.gender}
State: ${form.state}, District: ${form.district}, College: ${form.college}
Nearest Metro: ${form.nearestMetro || "Unknown"}, Area: ${form.areaType}
Education: ${form.qualification} — ${form.courseName}, CGPA: ${form.cgpa}, Year: ${form.yearCompletion}
Backlogs: ${form.backlogs || "None"}, Study Mode: ${form.studyMode}
Family Income: ${form.parentIncome}, Occupation: ${form.familyOccupation}
Financial Support: ${form.financialSupport}, Category: ${form.category}
Technical Skills: ${form.techSkills?.join(", ") || "None"}
Soft Skills: ${form.softSkills?.join(", ") || "None"}
Languages: ${form.languages?.join(", ") || "None"}
Experience: ${form.experience}, Certifications: ${form.certification}
Interested Field: ${form.interestedField}
Career Goal: ${form.careerGoal}
Work Preference: ${form.workType?.join(", ") || "Any"}
Personality: ${form.personality?.join(", ") || "Not specified"}
Relocate: ${form.relocate}, Target Salary: ${form.targetSalary}
Timeline: ${form.timeline}

Return ONLY valid JSON (no markdown, no explanation) with this exact structure:
{
  "topCareer": "Best career title",
  "successProbability": 80,
  "riskLevel": "Low",
  "aiSummary": "2-3 sentence personalized prediction",
  "fullAnalysis": "Detailed 150-200 word paragraph analysis",
  "careerScores": [{"name":"Career","score":85},{"name":"Career2","score":70},{"name":"Career3","score":65},{"name":"Career4","score":55},{"name":"Career5","score":40}],
  "salaryData": [{"year":"Now","salary":4.5},{"year":"2yr","salary":8},{"year":"5yr","salary":16},{"year":"10yr","salary":30}],
  "skillRadar": [{"skill":"Technical","A":70,"B":90},{"skill":"Soft Skills","A":65,"B":80},{"skill":"Domain","A":55,"B":85},{"skill":"Communication","A":70,"B":85},{"skill":"Leadership","A":50,"B":75}],
  "pieData": [{"name":"Sector","value":40},{"name":"Sector2","value":25},{"name":"Sector3","value":20},{"name":"Sector4","value":10},{"name":"Sector5","value":5}],
  "strengths": ["Strength 1","Strength 2","Strength 3","Strength 4"],
  "skillGaps": ["Gap 1","Gap 2","Gap 3","Gap 4"],
  "roadmap": [
    {"phase":"Month 1-2","title":"Foundation","steps":["Step1","Step2","Step3"]},
    {"phase":"Month 3-4","title":"Build","steps":["Step1","Step2","Step3"]},
    {"phase":"Month 5-6","title":"Launch","steps":["Step1","Step2","Step3"]},
    {"phase":"Year 1+","title":"Growth","steps":["Step1","Step2","Step3"]}
  ],
  "platforms": ["Platform1","Platform2","Platform3","Platform4","Platform5"],
  "scholarships": ["Scholarship1","Scholarship2","Scholarship3","Scholarship4","Scholarship5"],
  "trendingNow": ["Trend1","Trend2","Trend3","Trend4","Trend5"],
  "futureTrends": ["Future1","Future2","Future3","Future4","Future5"],
  "localDemand": ["Local1","Local2","Local3","Local4","Local5"],
  "globalDemand": ["Global1","Global2","Global3","Global4","Global5"]
}

All salary values in Indian LPA. Make everything hyper-personalized to this exact student's background.`;
}